import { useMemo } from 'react';

import { Flex, Headings, Table, TableLoadingRow, useIsDeviceSize } from '@foradevelopment/fora-ui';
import { InvoiceViewModel } from '@fora-monorepo/employer-my-pages-common/types';
import { ThunkRequestStatus } from '@fora-monorepo/webapp-types';

import { useTypedTranslation } from '../../../i18n/i18n';
import { InvoicesTableSortId } from '../../../common/types';
import { useAppSelector } from '../../../redux/hooks';
import { InvoicesTableSortIdKeys, InvoicesTableSortState } from './InvoicesListSortState';
import { InvoicesTableRow } from './InvoicesTableRow';

type InvoicesTableProps = {
  invoices: InvoiceViewModel[];
  invoicesListIndices: number[];
  sortTable: (sortId: InvoicesTableSortIdKeys) => void;
  sortState: InvoicesTableSortState;
};

export const InvoicesTable = ({ invoices, invoicesListIndices, sortTable, sortState }: InvoicesTableProps) => {
  const { t } = useTypedTranslation();
  const isLoadingInvoices = useAppSelector(state => state.invoice.loadingInvoices);
  const { isMobile, isTablet } = useIsDeviceSize();

  const sortableTableHeadCellsMobile: { name: string; id: InvoicesTableSortId }[] = [
    { name: t('table.invoice.columnInvoiceNumber'), id: InvoicesTableSortId.InvoiceOcrNumber },
    { name: t('table.invoice.columnInvoiceStatus'), id: InvoicesTableSortId.InvoiceStatus },
  ];
  const sortableTableHeadCellsTablet: { name: string; id: InvoicesTableSortId }[] = [
    { name: t('table.invoice.columnInvoiceNumber'), id: InvoicesTableSortId.InvoiceOcrNumber },
    { name: t('table.invoice.columnInvoiceStatus'), id: InvoicesTableSortId.InvoiceStatus },
    { name: t('list.invoiceEvents.invoiceDate'), id: InvoicesTableSortId.InvoiceDate },
    { name: t('table.invoice.columnInvoiceDueDate'), id: InvoicesTableSortId.InvoiceDueDate },
  ];
  const sortableTableHeadCellsDesktop: { name: string; id: InvoicesTableSortId }[] = [
    { name: t('table.invoice.columnInvoiceNumber'), id: InvoicesTableSortId.InvoiceOcrNumber },
    { name: t('table.invoice.columnInvoiceStatus'), id: InvoicesTableSortId.InvoiceStatus },
    { name: t('table.invoice.columnInvoiceType'), id: InvoicesTableSortId.InvoiceType },
    { name: t('list.invoiceEvents.invoiceDate'), id: InvoicesTableSortId.InvoiceDate },
    { name: t('table.invoice.columnInvoiceDueDate'), id: InvoicesTableSortId.InvoiceDueDate },
    { name: t('table.invoice.columnInvoiceAmount'), id: InvoicesTableSortId.InvoiceAmount },
  ];

  const tableColumns = useMemo(() => {
    if (isMobile) {
      return sortableTableHeadCellsMobile;
    } else if (isTablet) {
      return sortableTableHeadCellsTablet;
    } else {
      return sortableTableHeadCellsDesktop;
    }
    // Only trigger this hook when resolution changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, isTablet]);

  return (
    <Table withBorderedRows={true} withExpandableRows={!!invoices.length}>
      <Table.Head>
        <Table.HeadRow>
          {tableColumns.map((cell, index) => {
            return (
              <Table.HeadCell
                key={`${cell.id}-${index}`}
                sortId={cell.id}
                onToggle={sortTable as (id: string) => void}
                state={sortState[cell.id]}
                textAlign={index === tableColumns.length - 1 ? 'right' : 'left'}
              >
                {cell.name}
              </Table.HeadCell>
            );
          })}
        </Table.HeadRow>
      </Table.Head>
      <Table.Body>
        {invoices.length > 0 ? (
          isLoadingInvoices === ThunkRequestStatus.Pending ? (
            <TableLoadingRow />
          ) : (
            invoicesListIndices.map(invoiceListIndex => (
              <InvoicesTableRow key={`employment-table-row-${invoiceListIndex}`} invoice={invoices[invoiceListIndex]} />
            ))
          )
        ) : (
          <NoInvoices />
        )}
      </Table.Body>
    </Table>
  );
};

const NoInvoices = () => {
  const { t } = useTypedTranslation();
  return (
    <Table.Row>
      <Table.Cell colSpan={6}>
        <Flex justifyContent="center">
          <Headings.H6>{t('table.invoice.noRows')}</Headings.H6>
        </Flex>
      </Table.Cell>
    </Table.Row>
  );
};
